import React, { useState, useRef, useEffect } from 'react';
import Popover from '@mui/material/Popover';
import InfoIcon from '../../assets/icons/InfoIcon';
import PartScanningDetailsPopup from './PartScanningDetailsPopup';
import { axiosInstance } from '../../api/api';
import { N0_DATA_FOUND } from '../../constants';
import { useResolutionScale } from '../../hooks';

const greenColor = '#49a47e';
const redColor = 'red';
const greyColor = '#7070708c';
const yellowColor = '#ffa222';

const PartScanningDetailsPopover = ({ rowData }) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [colorCode, setColorCode] = useState(greyColor);
	const [partErrorMessage, setPartErrorMessage] = useState('');
	const [attributeErrorMessage, setAttributeErrorMessage] = useState('');
	const iconRef = useRef(null);

	const [partDetails, setPartDetails] = useState(null);
	const [attributeDetails, setAttributeDetails] = useState(null);

	// ── Single API call ───────────────────────────────────────────────────────
	const getStationOperations = (serialNumber) => {
		axiosInstance
			.get(`/i-button/station-operations/${serialNumber}`)
			.then(({ data }) => {
				if (!data || !Array.isArray(data) || data.length === 0) {
					setPartDetails({ stationGroups: [] });
					setAttributeDetails({ stationGroups: [] });
					setPartErrorMessage(N0_DATA_FOUND);
					setAttributeErrorMessage(N0_DATA_FOUND);
					return;
				}

				transformAndSetData(data);
			})
			.catch((err) => {
				setPartDetails({ stationGroups: [] });
				setAttributeDetails({ stationGroups: [] });
				setPartErrorMessage(N0_DATA_FOUND);
				setAttributeErrorMessage(N0_DATA_FOUND);
			});
	};
	// ─────────────────────────────────────────────────────────────────────────

	// ── Transform parts data ──────────────────────────────────────────────────
	const transformPartsData = (stationsArray) => {
		const stationGroups = [];

		stationsArray.forEach((station) => {
			const partFamiliesToCapture = station.partFamiliesToCapture || {};
			const partFamiliesCaptured = station.partFamiliesCaptured || {};
			const partAttributesToCapture = station.partAttributesToCapture || {};
			const partAttributesCaptured = station.partAttributesCaptured || {};

			const parts = [];

			// Part families
			Object.entries(partFamiliesToCapture).forEach(([pfId, required]) => {
				const captured = partFamiliesCaptured[pfId] || [];
				captured.forEach((scanned) => {
					parts.push({
						familyName: required.name,
						partNumber: required.partNumber,
						scannedValue: scanned.scannedSerialNumber || scanned.scannedPartNumber,
						skipReason: null,
						caPfId: required.caPfId,
					});
				});

				// If nothing captured yet, show as pending
				if (captured.length === 0) {
					parts.push({
						familyName: required.name,
						partNumber: required.partNumber,
						scannedValue: null,
						skipReason: null,
						caPfId: required.caPfId,
					});
				}
			});

			// Part attributes
			Object.entries(partAttributesToCapture).forEach(([paId, required]) => {
				const captured = partAttributesCaptured[paId];
				parts.push({
					familyName: required.caPaName,
					partNumber: '-',
					scannedValue: captured?.scannedValue || null,
					skipReason: null,
					caPfId: paId,
				});
			});

			if (parts.length > 0) {
				stationGroups.push({
					stationId: station.dtStationId,
					stationName: `Station ${station.dtStationId}`,
					parts,
				});
			}
		});

		return { stationGroups };
	};
	// ─────────────────────────────────────────────────────────────────────────

	// ── Transform machine data ────────────────────────────────────────────────
	const transformMachineData = (stationsArray) => {
		const stationGroups = [];

		stationsArray.forEach((station) => {
			const machineParametersToCapture = station.machineParametersToCapture || {};
			const machineParametersCaptured = station.machineParametersCaptured || {};

			const operations = [];

			Object.entries(machineParametersToCapture).forEach(([machineId, requiredParams]) => {
				const capturedList = machineParametersCaptured[machineId] || [];

				requiredParams.forEach((required) => {
					const matchingCaptured = capturedList.filter(
						(c) => c.parameterName === required.parameterName
					);

					if (matchingCaptured.length > 0) {
						matchingCaptured.forEach((result) => {
							const normalizedStatus = result.status?.toString().toUpperCase().trim();
							let status = 'Pending';
							let scannedValue = null;

							if (result.isSkipped) {
								status = 'Skipped';
								scannedValue = 'Skipped';
							} else if (normalizedStatus === 'OK') {
								status = 'OK';
								scannedValue = 'OK';
							} else if (normalizedStatus === 'NOK') {
								status = 'NOK';
								scannedValue = 'NOK';
							}

							operations.push({
								attributeName: result.parameterName,
								scannedValue,
								status,
								isSkipped: result.isSkipped || false,
								quantity: required.quantity,
								batchNumber: result.batchNumber,
							});
						});
					} else {
						// Not captured yet — pending
						operations.push({
							attributeName: required.parameterName,
							scannedValue: null,
							status: 'Pending',
							isSkipped: false,
							quantity: required.quantity,
							batchNumber: null,
						});
					}
				});
			});

			if (operations.length > 0) {
				stationGroups.push({
					stationId: station.dtStationId,
					stationName: `Station ${station.dtStationId}`,
					operations,
				});
			}
		});

		return { stationGroups };
	};
	// ─────────────────────────────────────────────────────────────────────────

	// ── Color logic ───────────────────────────────────────────────────────────
	const computeColor = (stationsArray) => {
		let isSkipReason = false;
		let hasPending = false;
		let hasScanned = false;

		stationsArray.forEach((station) => {
			// Parts
			const partFamiliesToCapture = station.partFamiliesToCapture || {};
			const partFamiliesCaptured = station.partFamiliesCaptured || {};
			const partAttributesToCapture = station.partAttributesToCapture || {};
			const partAttributesCaptured = station.partAttributesCaptured || {};

			Object.entries(partFamiliesToCapture).forEach(([pfId, required]) => {
				const captured = partFamiliesCaptured[pfId] || [];
				if (captured.length < (required.quantity || 1)) hasPending = true;
				captured.forEach((s) => {
					if (s.scannedSerialNumber || s.scannedPartNumber) hasScanned = true;
				});
			});

			Object.entries(partAttributesToCapture).forEach(([paId]) => {
				const captured = partAttributesCaptured[paId];
				if (!captured?.scannedValue) hasPending = true;
				else hasScanned = true;
			});

			// Machine
			const machineParametersToCapture = station.machineParametersToCapture || {};
			const machineParametersCaptured = station.machineParametersCaptured || {};

			Object.entries(machineParametersToCapture).forEach(([machineId, requiredParams]) => {
				const capturedList = machineParametersCaptured[machineId] || [];

				requiredParams.forEach((required) => {
					const matching = capturedList.filter(
						(c) => c.parameterName === required.parameterName
					);
					const completedCount = matching.filter(
						(r) => r.status === 'OK' || r.isSkipped
					).length;

					if (completedCount < (required.quantity || 1)) hasPending = true;

					matching.forEach((r) => {
						if (r.isSkipped) isSkipReason = true;
						if (r.status === 'OK') hasScanned = true;
						if (r.status === 'NOK') hasPending = true;
					});
				});
			});
		});

		if (hasPending) return yellowColor;
		if (isSkipReason) return redColor;
		if (hasScanned) return greenColor;
		return greyColor;
	};
	// ─────────────────────────────────────────────────────────────────────────

	const transformAndSetData = (stationsArray) => {
		setPartDetails(transformPartsData(stationsArray));
		setAttributeDetails(transformMachineData(stationsArray));
		setColorCode(computeColor(stationsArray));
	};

	const openPopup = () => setIsPopupOpen(true);
	const closePopup = () => setIsPopupOpen(false);

	const handleIconClick = () => {
		const serialNumber = rowData?.serialNumber;
		if (serialNumber) {
			getStationOperations(serialNumber);
			openPopup();
		}
	};

	const { scaleValue } = useResolutionScale();

	const styles = {
		infoIconStyle: {
			fontSize: scaleValue(20),
			position: 'relative',
			top: scaleValue(4),
			cursor: 'pointer',
			color: colorCode,
		},
		popupBox: {
			padding: `${scaleValue(20)}px ${scaleValue(24)}px`,
			borderRadius: scaleValue(8),
			display: 'flex',
			flexDirection: 'column',
			color: '#707070',
			fontSize: scaleValue(14),
			width: scaleValue(600),
			minWidth: scaleValue(300),
			minHeight: scaleValue(200),
			maxWidth: scaleValue(800),
			boxSizing: 'border-box',
		},
		title: {
			fontSize: scaleValue(16),
			padding: `${scaleValue(20)}px 0 ${scaleValue(12)}px 0`,
		},
		close: {
			fontSize: scaleValue(10),
			marginRight: scaleValue(4),
			color: '#707070',
			float: 'right',
		},
	};

	return (
		<>
			<InfoIcon
				style={styles.infoIconStyle}
				ref={iconRef}
				onClick={handleIconClick}
			/>
			<Popover
				open={isPopupOpen}
				style={{ marginTop: scaleValue(10) }}
				anchorEl={iconRef.current}
				onClose={closePopup}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'center',
					horizontal: 'left',
				}}
			>
				<div style={styles.popupBox}>
					<PartScanningDetailsPopup
						parts={partDetails || {}}
						attributes={attributeDetails || {}}
						partErrorMessage={partErrorMessage}
						attributeErrorMessage={attributeErrorMessage}
					/>
				</div>
			</Popover>
		</>
	);
};

export default PartScanningDetailsPopover;